import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, UserCheck, SlidersHorizontal, Bookmark, X,
  Sunrise, Sun, Moon, Cookie, Coffee, ChevronRight, Loader2, Leaf, ShieldAlert, Lightbulb, UtensilsCrossed
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_META = [
  { id: 'lunes', label: 'Lunes', short: 'L', gradient: 'from-blue-500 to-sky-400', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  { id: 'martes', label: 'Martes', short: 'M', gradient: 'from-violet-500 to-purple-400', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  { id: 'miercoles', label: 'Miercoles', short: 'X', gradient: 'from-emerald-500 to-teal-400', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  { id: 'jueves', label: 'Jueves', short: 'J', gradient: 'from-amber-500 to-yellow-400', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  { id: 'viernes', label: 'Viernes', short: 'V', gradient: 'from-red-500 to-rose-400', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  { id: 'sabado', label: 'Sabado', short: 'S', gradient: 'from-pink-500 to-rose-400', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  { id: 'domingo', label: 'Domingo', short: 'D', gradient: 'from-orange-500 to-amber-400', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
];

const MEAL_META = {
  desayuno: { label: 'Desayuno', icon: Sunrise, color: 'amber', gradient: 'from-amber-500 to-orange-400' },
  merienda1: { label: 'Merienda 1', icon: Cookie, color: 'rose', gradient: 'from-rose-500 to-pink-400' },
  almuerzo: { label: 'Almuerzo', icon: Sun, color: 'emerald', gradient: 'from-emerald-500 to-green-400' },
  merienda2: { label: 'Merienda 2', icon: Cookie, color: 'violet', gradient: 'from-violet-500 to-purple-400' },
  cena: { label: 'Cena', icon: Moon, color: 'indigo', gradient: 'from-indigo-500 to-blue-400' },
};

const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

// Get a preview image from a day's data
const getDayPreviewImage = (dayData) => {
  if (!dayData) return null;
  for (const [key, val] of Object.entries(dayData)) {
    if (key === 'bebidas') continue;
    const platos = val?.platos || [];
    if (platos.length > 0 && platos[0].image_url) return resolveImg(platos[0].image_url);
  }
  return null;
};

// ==============================
// Day Grid (7 Days Overview)
// ==============================
const DayGrid = ({ diet, onSelectDay }) => {
  const selectedDays = diet.selected_days || [];
  const days = diet.days || {};

  return (
    <div className="grid grid-cols-7 gap-2 sm:gap-3" data-testid="day-grid">
      {DAYS_META.map(day => {
        const isIncluded = selectedDays.includes(day.id);
        const dayData = days[day.id];
        const previewImg = getDayPreviewImage(dayData);

        return (
          <button key={day.id}
            onClick={() => isIncluded && onSelectDay(day.id)}
            disabled={!isIncluded}
            className={`relative rounded-2xl overflow-hidden transition-all duration-300 group ${isIncluded ? 'cursor-pointer hover:scale-[1.04] hover:shadow-xl' : 'opacity-30 cursor-default'}`}
            data-testid={`day-card-${day.id}`}>
            {/* Image or gradient bg */}
            <div className="aspect-square relative">
              {previewImg && isIncluded ? (
                <img src={previewImg} alt={day.label} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${day.gradient}`} />
              )}
              {/* Overlay */}
              <div className={`absolute inset-0 ${isIncluded ? 'bg-gradient-to-t from-black/70 via-black/20 to-transparent' : 'bg-black/40'}`} />
              {/* Label */}
              <div className="absolute inset-x-0 bottom-0 p-2 text-center">
                <p className="text-white font-black text-xs sm:text-sm leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{day.short}</p>
                <p className="text-white/80 text-[9px] sm:text-[10px] font-medium hidden sm:block">{day.label}</p>
              </div>
              {/* Arrow on hover */}
              {isIncluded && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight size={12} className="text-white" />
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ==============================
// Plato Detail Card (Restaurant style)
// ==============================
const PlatoDetailCard = ({ plato }) => {
  const imgUrl = resolveImg(plato.image_url);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`plato-detail-${plato.id}`}>
      {/* Image */}
      <div className="h-44 sm:h-52 bg-gray-100 overflow-hidden relative">
        {imgUrl ? (
          <img src={imgUrl} alt={plato.nombre} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gradient-to-br from-gray-100 to-gray-200">
            <Sun size={40} />
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4 space-y-3">
        <p className="font-black text-lg text-[#1A2E10] leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{plato.nombre}</p>

        {plato.descripcion && (
          <p className="text-sm text-gray-600 leading-relaxed">{plato.descripcion}</p>
        )}

        {/* Ingredients */}
        {plato.ingredientes && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Ingredientes</p>
            <div className="flex flex-wrap gap-1.5">
              {plato.ingredientes.split(',').map((ing, i) => (
                <span key={`ing-${ing.trim()}`} className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-lg border border-emerald-100">{ing.trim()}</span>
              ))}
            </div>
          </div>
        )}

        {/* Info row */}
        <div className="flex gap-2 flex-wrap">
          {plato.tipo_comida && <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-lg border border-amber-200">{plato.tipo_comida}</span>}
          {plato.etiquetas && <span className="text-[10px] font-bold bg-violet-50 text-violet-700 px-2 py-0.5 rounded-lg border border-violet-200">{plato.etiquetas}</span>}
          {plato.costo_promedio && <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-lg border border-blue-200">{plato.costo_promedio}</span>}
          {plato.calorias !== undefined && <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">{plato.calorias} kcal</span>}
        </div>

        {/* Recipe */}
        {plato.receta && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Receta</p>
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              {plato.receta.split(/Paso \d+:/).filter(Boolean).map((paso, i) => (
                <div key={`paso-${i + 1}`} className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#467322] text-white text-[10px] flex items-center justify-center flex-shrink-0 font-bold">{i + 1}</span>
                  <p className="text-xs text-gray-700 leading-relaxed mt-0.5">{paso.trim()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==============================
// Day Detail View (Restaurant Menu)
// ==============================
const DayDetailView = ({ dayId, dayData, selectedMeals, onBack, platosDb }) => {
  const dayMeta = DAYS_META.find(d => d.id === dayId);

  // Enrich platos with full DB data
  const enrichPlato = (p) => {
    const full = platosDb.find(x => x.id === p.id);
    return full ? { ...p, ...full } : p;
  };

  return (
    <div className="animate-fade-in" data-testid="day-detail-view">
      {/* Header */}
      <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-[#467322] text-sm font-semibold mb-4 transition-colors" data-testid="day-detail-back">
        <ArrowLeft size={16} /> Volver a vista general
      </button>
      <div className={`bg-gradient-to-r ${dayMeta?.gradient || 'from-gray-500 to-gray-600'} rounded-2xl p-5 mb-6 shadow-lg`}>
        <p className="text-white/70 text-xs font-bold uppercase tracking-wider">Menu del dia</p>
        <p className="text-white text-3xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{dayMeta?.label || dayId}</p>
      </div>

      {/* Meals */}
      <div className="space-y-8">
        {(selectedMeals || []).map(mealId => {
          const meta = MEAL_META[mealId];
          if (!meta) return null;
          const mealData = dayData?.[mealId];
          const mealPlatos = (mealData?.platos || []).map(enrichPlato);
          const IconComp = meta.icon;

          return (
            <div key={mealId} data-testid={`meal-section-${mealId}`}>
              {/* Meal header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-lg`}>
                  <IconComp size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-black text-xl text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{meta.label}</p>
                  <p className="text-xs text-gray-400">{mealPlatos.length} plato{mealPlatos.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {/* Plato cards */}
              {mealPlatos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mealPlatos.map((p, i) => <PlatoDetailCard key={p.id || i} plato={p} />)}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-400 text-sm">
                  No hay platos seleccionados
                </div>
              )}
            </div>
          );
        })}

        {/* Bebidas */}
        {(dayData?.bebidas || []).length > 0 && (
          <div data-testid="meal-section-bebidas">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <Coffee size={18} className="text-white" />
              </div>
              <div>
                <p className="font-black text-xl text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Bebidas del Dia</p>
                <p className="text-xs text-gray-400">{dayData.bebidas.length} bebida{dayData.bebidas.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {dayData.bebidas.map((b, i) => {
                const imgUrl = resolveImg(b.image_url);
                return (
                  <div key={b.id || i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`bebida-card-${b.id}`}>
                    <div className="h-20 bg-gray-100 overflow-hidden">
                      {imgUrl && <img src={imgUrl} alt={b.nombre} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                    </div>
                    <div className="p-2.5">
                      <p className="font-bold text-xs text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{b.nombre}</p>
                      {b.calorias !== undefined && <p className="text-[10px] text-gray-400 mt-0.5">{b.calorias} kcal</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ==============================
// Main ClientDietPage
// ==============================
const ClientDietPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = `/${user?.user_type || 'client'}`;
  const [activeDiet, setActiveDiet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangeMenu, setShowChangeMenu] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [platosDb, setPlatosDb] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [dietRes, platosRes] = await Promise.all([
          axios.get(`${API}/diet/active`),
          axios.get(`${API}/diet/catalog/platos`).catch(() => ({ data: [] })),
        ]);
        if (dietRes.data.active) {
          const diet = dietRes.data;
          // Normalize: trainer diets may use meals_data instead of days
          if (diet.meals_data && !diet.days) {
            diet.days = diet.meals_data;
          }
          setActiveDiet(diet);
        }
        setPlatosDb(platosRes.data || []);
      } catch (error) { console.error('Failed to load diet data:', error); }
      setLoading(false);
    };
    load();
  }, []);

  const changeOptions = [
    { icon: UserCheck, title: 'Dieta recomendada por tu entrenador', gradient: 'from-emerald-500 to-teal-500', shadow: 'shadow-emerald-500/20', testId: 'change-trainer' },
    { icon: Bookmark, title: 'Dietas guardadas', gradient: 'from-violet-500 to-purple-500', shadow: 'shadow-violet-500/20', testId: 'change-saved', action: () => { setShowChangeMenu(false); navigate(`${basePath}/diet/saved`); } },
    { icon: SlidersHorizontal, title: 'Configurador de dieta', gradient: 'from-blue-500 to-indigo-500', shadow: 'shadow-blue-500/20', testId: 'change-configurator', action: () => { setShowChangeMenu(false); navigate(`${basePath}/diet/configurator`); } },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  // ===== ACTIVE CUSTOM DIET VIEW =====
  if (activeDiet && ['custom', 'trainer'].includes(activeDiet.type) && activeDiet.days) {
    const isDetailView = selectedDayId !== null;

    return (
      <div className="min-h-[60vh] pb-8" data-testid="client-diet-page">
        {/* Header */}
        <div className="max-w-6xl mx-auto flex items-center justify-between py-3 sticky top-14 bg-zinc-50/90 backdrop-blur-xl z-30">
          <button onClick={() => isDetailView ? setSelectedDayId(null) : navigate(`${basePath}/health`)}
            className="flex items-center gap-2 text-[#467322] font-semibold text-sm hover:text-[#1A2E10] transition-colors" data-testid="back-to-health">
            <ArrowLeft size={18} /> {isDetailView ? 'Mi Dieta' : 'Mi Cuerpo'}
          </button>
          <button onClick={() => setShowChangeMenu(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:text-[#467322] hover:border-[#467322]/30 transition-all"
            data-testid="change-diet-btn">
            Cambiar dieta
          </button>
        </div>

        <div className="max-w-6xl mx-auto mt-2">
          {!isDetailView ? (
            <>
              {/* Title */}
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-wider text-[#467322]">Tu plan activo</p>
                <h1 className="text-3xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{activeDiet.name}</h1>
                <p className="text-sm text-gray-500 mt-1">{(activeDiet.selected_days || []).length} dias &middot; {(activeDiet.selected_meals || []).length} comidas por dia</p>
              </div>
              {/* 7-day grid */}
              <DayGrid diet={activeDiet} onSelectDay={setSelectedDayId} />

              {/* Info subcarpetas */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { id: 'vegetales', label: 'Vegetales', desc: 'Lo que puedes comer', icon: Leaf, gradient: 'from-emerald-500 to-teal-400', shadow: 'shadow-emerald-500/20' },
                  { id: 'prohibidos', label: 'Prohibidos', desc: 'Lo que debes evitar', icon: ShieldAlert, gradient: 'from-red-500 to-rose-400', shadow: 'shadow-red-500/20' },
                  { id: 'tips', label: 'Tips', desc: 'Recomendaciones de salud', icon: Lightbulb, gradient: 'from-blue-500 to-sky-400', shadow: 'shadow-blue-500/20' },
                ].map(item => (
                  <button key={item.id} onClick={() => setActiveSection(activeSection === item.id ? null : item.id)}
                    className={`bg-white rounded-2xl border p-4 text-left hover:shadow-md transition-all group ${activeSection === item.id ? 'border-[#467322] shadow-md ring-1 ring-[#467322]/20' : 'border-gray-100 hover:border-gray-200'}`}
                    data-testid={`section-${item.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg ${item.shadow}`}>
                        <item.icon size={18} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-sm text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{item.label}</p>
                        <p className="text-xs text-gray-400">{item.desc}</p>
                      </div>
                      <ChevronRight size={16} className={`transition-transform ${activeSection === item.id ? 'rotate-90 text-[#467322]' : 'text-gray-300 group-hover:text-[#467322]'}`} />
                    </div>
                  </button>
                ))}
              </div>

              {/* Section content */}
              {activeSection === 'vegetales' && (
                <div className="mt-4 bg-white rounded-2xl border border-emerald-200/60 shadow-sm overflow-hidden animate-fade-in" data-testid="vegetales-card">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-400 px-5 py-3.5 flex items-center gap-2.5">
                    <Leaf size={18} className="text-white" />
                    <p className="text-white text-lg font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Vegetales que puedes comer</p>
                  </div>
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                    {['Brocoli y coliflor', 'Rabano', 'Apio', 'Pepino', 'Zanahoria', 'Repollo y berro', 'Tomate'].map((veg) => (
                      <div key={veg} className="flex items-center gap-2.5 bg-emerald-50/60 rounded-xl border border-emerald-100/60 px-3.5 py-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                        <p className="text-[13px] font-medium text-[#1A2E10]">{veg}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === 'prohibidos' && (
                <div className="mt-4 bg-white rounded-2xl border border-red-200/60 shadow-sm overflow-hidden animate-fade-in" data-testid="no-consumir-card">
                  <div className="bg-gradient-to-r from-red-500 to-rose-400 px-5 py-3.5 flex items-center gap-2.5">
                    <ShieldAlert size={18} className="text-white" />
                    <p className="text-white text-lg font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>No Consumir</p>
                  </div>
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { label: 'Granos y harinas', items: 'Pan, avena, harina de trigo y de maiz, granolas y cereales, arroz' },
                      { label: 'Frutas', items: 'No consumir guineos' },
                      { label: 'Bebidas', items: 'Azucar, cervezas, ron, cocteles' },
                      { label: 'Alcohol', items: 'No consumir alcohol de ningun tipo' },
                      { label: 'Aceites', items: 'Aceites de canola, maiz, soya, girasol' },
                      { label: 'Lacteos', items: 'Leches, yogures y/o helados descremados' },
                    ].map(cat => (
                      <div key={cat.label} className="flex items-start gap-3 bg-red-50/60 rounded-xl border border-red-100/60 p-3.5">
                        <span className="w-2 h-2 rounded-full bg-red-400 mt-[7px] flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-red-500 mb-0.5">{cat.label}</p>
                          <p className="text-[13px] font-medium text-[#1A2E10] leading-snug">{cat.items}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === 'tips' && (
                <div className="mt-4 bg-white rounded-2xl border border-blue-200/60 shadow-sm overflow-hidden animate-fade-in" data-testid="recomendaciones-card">
                  <div className="bg-gradient-to-r from-blue-500 to-sky-400 px-5 py-3.5 flex items-center gap-2.5">
                    <Lightbulb size={18} className="text-white" />
                    <p className="text-white text-lg font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Recomendaciones</p>
                  </div>
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      'Ayunos de 12 horas diarias',
                      'Preferencias vegetales: brocoli, coliflor, lechuga, apio, berro',
                      'Antes de cada desayuno, una cucharada de aceite de coco',
                      'Aderezar: sal, vinagre y aceite verde',
                      'Consumir maximo 2 litros de agua al dia',
                      '30 minutos de exposicion al sol en la manana y 30 minutos en la tarde',
                      'Cocinar con aceite de coco y/o mantequilla y GHEE. Grasa de cerdo (lardo)',
                      '5 mil pasos cada dia, monitorear con la APP pacer',
                      'Utilizar jengibre y canela con el cafe si es de tu gusto',
                      'Dos tazas de cafe al dia maximo, en su defecto te verde',
                      'Tomar una cucharada de aceite de coco justo antes de cada comida',
                      '3 cucharadas al dia maximo',
                      'Tratar de cenar mas tarde 8:30 p.m.',
                      'Guarniciones Carbs: auyama, platano verde (1), batata, yautia',
                    ].map((tip, i) => (
                      <div key={`tip-${i}`} className="flex items-start gap-3 bg-blue-50/60 rounded-xl border border-blue-100/60 p-3.5">
                        <span className="w-2 h-2 rounded-full bg-blue-400 mt-[7px] flex-shrink-0" />
                        <p className="text-[13px] font-medium text-[#1A2E10] leading-snug">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <DayDetailView
              dayId={selectedDayId}
              dayData={activeDiet.days?.[selectedDayId]}
              selectedMeals={activeDiet.selected_meals}
              onBack={() => setSelectedDayId(null)}
              platosDb={platosDb}
            />
          )}
        </div>

        {/* Change Diet Modal */}
        {showChangeMenu && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center animate-fade-in" onClick={() => setShowChangeMenu(false)}>
            <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="change-diet-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Cambiar Dieta</h3>
                <button onClick={() => setShowChangeMenu(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-400" /></button>
              </div>
              <div className="space-y-3">
                {changeOptions.map(opt => (
                  <button key={opt.testId} onClick={() => { if (opt.action) opt.action(); }} className="w-full bg-white rounded-2xl border border-gray-100/50 p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group" data-testid={opt.testId}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${opt.gradient} flex items-center justify-center shadow-lg ${opt.shadow}`}>
                        <opt.icon size={18} className="text-white" />
                      </div>
                      <p className="font-semibold text-sm text-gray-700 group-hover:text-[#1A2E10] transition-colors">{opt.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== NO DIET / NO CUSTOM DIET STATE =====
  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-24" data-testid="client-diet-page">
      <div className="text-center py-12">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mx-auto mb-5">
          <Sun size={36} className="text-emerald-600" />
        </div>
        <h2 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mi Dieta</h2>
        <p className="text-gray-500 text-sm mt-2 max-w-sm mx-auto">Configura tu plan nutricional personalizado o activa una dieta guardada</p>
      </div>

      <div className="space-y-3 max-w-md mx-auto">
        {[
          { icon: SlidersHorizontal, title: 'Configurador de Plan Nutricional', description: 'Crea tu propia dieta paso a paso segun tus necesidades', gradient: 'from-blue-500 to-indigo-500', shadow: 'shadow-blue-500/20', testId: 'diet-configurator', action: () => navigate(`${basePath}/diet/configurator`) },
          { icon: Bookmark, title: 'Dietas Guardadas', description: 'Activa una dieta que hayas creado anteriormente', gradient: 'from-violet-500 to-purple-500', shadow: 'shadow-violet-500/20', testId: 'diet-saved', action: () => navigate(`${basePath}/diet/saved`) },
          { icon: UserCheck, title: 'Plan de tu Entrenador', description: 'Ver las dietas asignadas por tu nutricionista', gradient: 'from-emerald-500 to-teal-500', shadow: 'shadow-emerald-500/20', testId: 'diet-trainer', action: () => navigate(`${basePath}/diet/saved?filter=trainer`) },
        ].map(opt => (
          <button key={opt.testId} onClick={() => { if (opt.action) opt.action(); }}
            className="w-full bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group"
            data-testid={opt.testId}>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${opt.gradient} flex items-center justify-center shadow-lg ${opt.shadow} group-hover:scale-105 transition-transform`}>
                <opt.icon size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-base text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{opt.title}</p>
                <p className="text-xs text-gray-500 mt-1">{opt.description}</p>
              </div>
              <ChevronRight size={18} className="text-gray-300 group-hover:text-[#467322] transition-colors" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ClientDietPage;
